import * as React from 'react';
import { useDispatch } from 'react-redux';
import { setSelectedSailIndex } from '../../actions/cruisePageActions';
import * as actionTypes from '../../consts/actionTypes';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';
import { ISail, ISoldOutBooking } from '../../interfaces';
import { CruisePageActionCreators } from '../../reducers/cruisePage/action-creators';
import { __ } from '../../helpers/TranslationService';
import { CalendarIcon } from '../Icons/CalendarIcon';
import { RequestFormSelect } from './RequestFormSelect';
import { IExchangeRate } from '../../interfaces/IHeader';
import { ISailRates } from '../../interfaces';
import { convertToDualCurrency, getPerPersonPriceString } from '../../helpers/priceHelpers';

interface IRequestFormDates {
    sails: ISail[];
    selectedSailIndex: number;
    setSelectedSailIndex: typeof setSelectedSailIndex;
    exchangeRate: IExchangeRate;

    ratesBySail: Record<string, ISailRates>;
    currency: string;
    defaultCurrency: string;
    passengersNum: number;
    soldOutBooking: ISoldOutBooking;
}

interface IDatePickerOption {
    label: string;
    nid: number;
}

const RequestFormDates = (props: IRequestFormDates) => {
    const {
        sails,
        selectedSailIndex,
        exchangeRate,
        defaultCurrency,
        currency,
        soldOutBooking,
        passengersNum,
        ratesBySail,
    } = props;
    const dispatch = useDispatch();

    const handleDateChange = (nid: string) => {
        const selectedSailNid = nid;
        const selectedSailIndex = sails.findIndex(
            (sail) => sail.nid === parseInt(selectedSailNid, 10),
        );
        props.setSelectedSailIndex(selectedSailIndex);
        window.history.replaceState(undefined, undefined, `#selectedSail=${selectedSailNid}`);

        const data = sails[selectedSailIndex];
        dispatch({
            type: actionTypes.updateRequestFormFields,
            payload: { sailNid: selectedSailNid },
        });
        eventTracker.track({
            category: 'cruisepage-selected date',
            action: 'select-date',
            label: data.label,
        });
    };

    const selectedValue = sails[selectedSailIndex]?.nid;

    const isDualCurrency = exchangeRate !== null;

    const sailsOptionsByYear = sails.reduce((result, sail) => {
        const date = new Date(sail.departure * 1000);
        const year = date.getFullYear();

        const dualCurrencyLabel =
            isDualCurrency &&
            getPerPersonPriceString(
                convertToDualCurrency(exchangeRate, ratesBySail[sail.nid]?.minPrice),
                defaultCurrency,
                passengersNum,
                soldOutBooking,
            ) + ' / ';

        const defaultLabel = getPerPersonPriceString(
            ratesBySail[sail.nid]?.minPrice,
            currency,
            passengersNum,
            soldOutBooking,
        );

        const sailOption: IDatePickerOption = {
            label: `${sail.label} (${isDualCurrency ? dualCurrencyLabel : ''}${defaultLabel})`,
            nid: sail.nid,
        };

        if (!result[year]) result[year] = [];
        result[year].push(sailOption);
        return result;
    }, {});

    return (
        <div className="requestFormContainer__dates">
            <div className="requestFormContainer__section-label">
                {__('general.chooseDate', 'dreamlines')}
            </div>

            <div className="datesSelectWrapper">
                <RequestFormSelect
                    key={selectedValue}
                    id="sail-dates-select"
                    onChange={handleDateChange}
                    defaultValue={selectedValue}
                    icon={<CalendarIcon width={21} height={21} />}
                >
                    {Object.entries(sailsOptionsByYear).map(
                        ([year, sails]: [string, IDatePickerOption[]], i) => {
                            return (
                                <optgroup label={year} key={`dates-group-year-${i}`}>
                                    {sails?.map((opt, i) => {
                                        return (
                                            <option value={opt.nid} key={`dates-${i}`}>
                                                {opt.label}
                                            </option>
                                        );
                                    })}
                                </optgroup>
                            );
                        },
                    )}
                </RequestFormSelect>
            </div>
        </div>
    );
};
export default RequestFormDates;
